/**
 * One-shot modal that displays a freshly-minted API key. Stashes the raw
 * key in sessionStorage so the merchant can use it elsewhere in the dashboard
 * (e.g. the Webhooks screen needs it for Bearer auth on /api/v1 calls)
 * within the same session — but never persists across reloads.
 */

import { useState } from "react";
import type { MintedApiKey } from "../lib/api";
import { useT } from "../lib/i18n";

const SESSION_KEY = "shieldz.api_key";

export const getStoredApiKey = (): string | null =>
  sessionStorage.getItem(SESSION_KEY);

export const setStoredApiKey = (raw: string): void =>
  sessionStorage.setItem(SESSION_KEY, raw);

export const clearStoredApiKey = (): void =>
  sessionStorage.removeItem(SESSION_KEY);

export function RawKeyModal({
  mintedKey,
  onDismiss,
}: {
  mintedKey: MintedApiKey;
  onDismiss: () => void;
}) {
  const t = useT();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const display = revealed
    ? mintedKey.raw_key
    : `${mintedKey.prefix}${"•".repeat(38)}${mintedKey.last4}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(mintedKey.raw_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* navigator.clipboard not available */
    }
  };

  const handleDismiss = () => {
    // Cache for the current tab — webhook screens reuse it.
    setStoredApiKey(mintedKey.raw_key);
    onDismiss();
  };

  return (
    <div className="card">
      <div className="h2">{t("rawkey.title")}</div>
      <div className="banner banner-warn">
        {t("rawkey.warning_pre")} <code>.env</code> {t("rawkey.warning_post")}
      </div>
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          padding: "10px 12px",
          background: "var(--bg)",
          borderRadius: 8,
          border: "1px solid var(--border)",
          wordBreak: "break-all",
        }}
      >
        {display}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          className="btn-secondary"
          onClick={() => setRevealed((v) => !v)}
        >
          {revealed ? t("rawkey.hide") : t("rawkey.reveal")}
        </button>
        <button onClick={() => void handleCopy()}>
          {copied ? t("rawkey.copied") : t("rawkey.copy")}
        </button>
      </div>
      <div className="muted" style={{ marginTop: 12 }}>
        {t("rawkey.server_code_intro")}
        <pre
          style={{
            marginTop: 6,
            padding: 10,
            background: "var(--bg)",
            borderRadius: 8,
            fontSize: 11,
            overflow: "auto",
          }}
        >
{`curl -X POST https://shieldz.cash/api/v1/invoices \\
  -H "Authorization: Bearer $SHIELDZ_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount_usd_cents": 1000}'`}
        </pre>
      </div>
      <button onClick={handleDismiss} style={{ marginTop: 16 }}>
        {t("rawkey.saved_continue")}
      </button>
    </div>
  );
}
